/* global globalThis */
import url from "./apiURL";
import { authenticatedFetch } from "../../utils/authenticatedFetch";
import { getValidToken } from "../../utils/getValidToken";

/**
 * Smart Source Lens Filters API call
 * @param {*} payload
 * @returns
 */

const isAbortError = (error) =>
  error?.name === "AbortError" ||
  error?.message?.toLowerCase?.().includes("aborted");
export const fetchSmartSourceLensFilters = async (payload, signal) => {
  try {
    return await authenticatedFetch(url.SmartScourceLensFilters, {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    console.error("Error fetching Smart Source Lens filters:", error);
    throw error;
  }
};

const formatExportFileNamePart = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "_")
    .replace(/^_+/, "")
    .replace(/_$/, "");

const buildSmartSourceLensExportFileName = (screen,activeTab, uom) => {
  const formattedActiveTab = formatExportFileNamePart(activeTab);
  const formattedUom = formatExportFileNamePart(uom);

  return `${screen}${formattedActiveTab}_${formattedUom}.xlsx`;
};

export const fetchSmartSourceLensRows = async (payload, signal) => {
  try {
    return await authenticatedFetch(url.SmartScourceLensRows, {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    console.error("Error fetching Smart Source Lens rows:", error);
    throw error;
  }
};

export const fetchSmartSourceLensCount = async (payload, signal) => {
  try {
    return await authenticatedFetch(url.SmartScourceLensCount, {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    console.error("Error fetching Smart Source Lens count:", error);
    throw error;
  }
};

export const fetchSmartSourceLensTotals = async (payload, signal) => {
  try {
    return await authenticatedFetch(url.SmartScourceLensTotals, {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    console.error("Error fetching Smart Source Lens total:", error);
    throw error;
  }
};

export const fetchSmartSourceLensKpi = async (payload, signal) => {
  try {
    return await authenticatedFetch(url.SmartScourceLensKpi, {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    console.error("Error fetching Smart Source Lens kpi:", error);
    throw error;
  }
};

export const fetchSmartScourceLensPdqAllocationTable = async (
  payload,
  signal,
) => {
  try {
    return await authenticatedFetch(url.SmartScourceLensPdqAllocationTable, {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    console.error(
      "Error fetching Smart Source Lens pdq allocation table:",
      error,
    );
    throw error;
  }
};

export const fetchSmartScourceLensHistoricalGraph = async (payload, signal) => {
  try {
    return await authenticatedFetch(url.SmartScourceLensHistoricalGraph, {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    console.error("Error fetching Smart Source Lens historical graph:", error);
    throw error;
  }
};

export const fetchSmartScourceLensExportData = async (payload, activeTab) => {
  try {
    const token = await getValidToken();

    const response = await fetch(url.SmartScourceLensExportData, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = globalThis.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute(
      "download",
      buildSmartSourceLensExportFileName("smart_source_lens_",activeTab, payload.uom),
    );

    document.body.appendChild(link);
    link.click();

    link.remove();
    globalThis.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    console.error("Error fetching Smart Source Lens export excel API:", error);
    throw error;
  }
};

const dcSupplyFetch = async (urlStr, payload, signal) => {
  const data = await authenticatedFetch(urlStr, {
    method: "POST",
    body: JSON.stringify(payload),
    signal,
  });
  return data;
};

export const fetchDcSupplyInsightsFilters = async (payload, signal) => {
  try {
    return await dcSupplyFetch(url.DcSupplyInsightsFilters, payload, signal);
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error("Error fetching Dc Supply Insights Filters:", error);
    throw error;
  }
};

export const fetchDcSupplyInsightsRows = async (payload, signal) => {
  try {
    return await dcSupplyFetch(url.DcSupplyInsightsRows, payload, signal);
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error("Error fetching Dc Supply Insights rows:", error);
    throw error;
  }
};

export const fetchDcSupplyInsightsCount = async (payload, signal) => {
  try {
    return await dcSupplyFetch(url.DcSupplyInsightsCount, payload, signal);
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error("Error fetching Dc Supply Insights Count:", error);
    throw error;
  }
};

export const fetchDcSupplyInsightsTotals = async (payload, signal) => {
  try {
    return await dcSupplyFetch(url.DcSupplyInsightsTotals, payload, signal);
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error("Error fetching Dc Supply Insights total:", error);
    throw error;
  }
};

export const fetchDcSupplyInsightsExportData = async (payload, activeTab) => {
  try {
    const token = await getValidToken();

    const response = await fetch(url.DcSupplyInsightsExportData, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = globalThis.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute(
      "download",
      buildSmartSourceLensExportFileName("dc_supply_insights_",activeTab, payload.uom),
    );

    document.body.appendChild(link);
    link.click();

    link.remove();
    globalThis.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    console.error("Error fetching DC Supply Insights export excel API:", error);
    throw error;
  }
};

export const fetchDcSupplyInsightsHistoricalGraph = async (payload, signal) => {
  try {
    return await dcSupplyFetch(
      url.DcSupplyInsightsHistoricalGraph,
      payload,
      signal,
    );
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error(
      "Error fetching Dc Supply Insights summary historical graph:",
      error,
    );
    throw error;
  }
};

export const fetchDcSupplyInsightsNrMitigationTable = async (
  payload,
  signal,
) => {
  try {
    return await dcSupplyFetch(
      url.DcSupplyInsightsNrMitigationTable,
      payload,
      signal,
    );
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error(
      "Error fetching Dc Supply Insights NR Mitigation table:",
      error,
    );
    throw error;
  }
};

export const fetchDcSupplyInsightsNrSufficiencyGraph = async (
  payload,
  signal,
) => {
  try {
    return await dcSupplyFetch(
      url.DcSupplyInsightsNrSufficiencyGraph,
      payload,
      signal,
    );
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error(
      "Error fetching Dc Supply Insights summary historical graph:",
      error,
    );
    throw error;
  }
};

export const fetchDcSupplyInsightsAllocToPdqGraph = async (payload, signal) => {
  try {
    return await dcSupplyFetch(
      url.DcSupplyInsightsAllocToPdqGraph,
      payload,
      signal,
    );
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error(
      "Error fetching Dc Supply Insights summary historical graph:",
      error,
    );
    throw error;
  }
};

export const fetchDcSupplyInsightsNrAllocDropGraph = async (
  payload,
  signal,
) => {
  try {
    return await dcSupplyFetch(
      url.DcSupplyInsightsNrAllocDropGraph,
      payload,
      signal,
    );
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error(
      "Error fetching Dc Supply Insights summary historical graph:",
      error,
    );
    throw error;
  }
};

export const fetchDcSupplyInsightsNrDestNrApoDropGraph = async (
  payload,
  signal,
) => {
  try {
    return await dcSupplyFetch(
      url.DcSupplyInsightsNrDestNrApoDropGraph,
      payload,
      signal,
    );
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error(
      "Error fetching Dc Supply Insights summary historical graph:",
      error,
    );
    throw error;
  }
};

export const fetchDcSupplyInsightsBucketsOverviewGraph = async (
  payload,
  signal,
) => {
  try {
    return await dcSupplyFetch(
      url.DcSupplyInsightsBucketsOverviewGraph,
      payload,
      signal,
    );
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error(
      "Error fetching Dc Supply Insights buckets overview graph:",
      error,
    );
    throw error;
  }
};
